import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { InventoryTransferModel } from 'src/app/models/inventory/inventory-transfer.model';
import { InventoryTransferPrintModel } from 'src/app/models/print/inventory/inventory-transfer-print.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InventoryTransferTableModel } from 'src/app/models/tables/inventory/inventory-transfer-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InventoryTransferService {

  constructor(private _httpClient: HttpClient) { }

  annulInventoryTransfer(id: number): Observable<InventoryTransferModel> {
    return this._httpClient.put<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.AnnulInventoryTransfer}/${id}`,
      null);
  }

  confirmInventoryTransfer(id: number): Observable<InventoryTransferModel> {
    return this._httpClient.put<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.ConfirmInventoryTransfer}/${id}`,
      null);
  }

  createInventoryTransfer(inventoryTransferContract: InventoryTransferModel): Observable<InventoryTransferModel> {
    return this._httpClient.post<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.CreateInventoryTransfer}`,
      inventoryTransferContract);
  }

  deleteInventoryTransfer(inventoryTransferId: number): Observable<InventoryTransferModel> {
    return this._httpClient.delete<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.DeleteInventoryTransfer}/${inventoryTransferId}`);
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams()
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getAllTransportersForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetAllTransportersForSelectAsync}`);
  }

  getAllVehiclesForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetAllVehiclesForSelectAsync}`);
  }

  getAllStoresForSelectAsync(inventoryId: number, branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('inventoryId', inventoryId)
      .set('branchId', branchId);

    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetAllStoresForSelectAsync}`,
      { params: params });
  }

  getConsecutiveEntryAndOutputByInventoryTransferId(inventoryTransferId: number): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetConsecutiveEntryAndOutputByInventoryTransferId}/${inventoryTransferId}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetColumnsForGeneralConsultationAsync}`);
  }

  getFirstInventoryTransferByKeysAsync(inventoryId: number): Observable<InventoryTransferModel> {
    const params = new HttpParams().set('inventoryId', inventoryId);

    return this._httpClient.get<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetFirstInventoryTransferByKeysAsync}`,
      { params: params });
  }

  getInventoryTransferById(id: number): Observable<InventoryTransferModel> {
    return this._httpClient.get<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetInventoryTransferById}/${id}`);
  }

  getInventoryTransferByKeysAsync(inventoryId: number, consecutive: number): Observable<InventoryTransferModel> {
    const params = new HttpParams()
      .set('inventoryId', inventoryId)
      .set('consecutive', consecutive);

    return this._httpClient.get<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetInventoryTransferByKeysAsync}`,
      { params: params }
    );
  }

  getEmptyInventoryTransferPrint(): Observable<InventoryTransferPrintModel> {
    return this._httpClient.get<InventoryTransferPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.getEmptyInventoryTransferPrint}`);
  }

  getInventoryTransferForPrint(companyId: number, consecutive: number, inventoryTransferId: number): Observable<InventoryTransferPrintModel> {
    const params = new HttpParams()
      .set('companyId', companyId)
      .set('consecutive', consecutive)
      .set('inventoryTransferId', inventoryTransferId);

    return this._httpClient.get<InventoryTransferPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetInventoryTransferForPrint}`,
      { params: params }
    );
  }

  getInventoryTransfersForGeneralConsultation(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<InventoryTransferTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<InventoryTransferTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetInventoryTransfersForGeneralConsultation}`,
      filters, { params: params }
    );
  }

  getLastInventoryTransferByKeysAsync(inventoryId: number): Observable<InventoryTransferModel> {
    const params = new HttpParams().set('inventoryId', inventoryId);

    return this._httpClient.get<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetLastInventoryTransferByKeysAsync}`,
      { params: params });
  }

  getNextInventoryTransferByKeysAsync(inventoryId: number, consecutive: number | null): Observable<InventoryTransferModel> {
    let params = new HttpParams().set('inventoryId', inventoryId);
    if (consecutive !== null)
      params = params.set('consecutive', consecutive.toString());

    return this._httpClient.get<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetNextInventoryTransferByKeysAsync}`,
      { params: params });
  }

  getPreviusInventoryTransferByKeysAsync(inventoryId: number, consecutive: number | null): Observable<InventoryTransferModel> {
    let params = new HttpParams().set('inventoryId', inventoryId);
    if (consecutive !== null)
      params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.GetPreviusInventoryTransferByKeysAsync}`,
      { params: params });
  }

  howManyTranfersInTransitByUserAuthenticated(): Observable<number> {
    return this._httpClient.get<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.HowManyTranfersInTransitByUserAuthenticated}`);
  }

  setStateInventoryTransfer(inventoryTransferId: number): Observable<InventoryTransferModel> {
    return this._httpClient.put<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.SetStateInventoryTransfer}/${inventoryTransferId}`,
      null);
  }

  updateInventoryTransfer(inventoryTransferModel: InventoryTransferModel): Observable<InventoryTransferModel> {
    return this._httpClient.put<InventoryTransferModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.inventoryTransfer.UpdateInventoryTransfer}`,
      inventoryTransferModel);
  }
}
